"use client";

import { Hint } from "@/components/ui/hint";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";
import React from "react";

type MetricCardProps = {
  title: string;
  icon: LucideIcon;
  value: React.ReactNode;
  /**
   * Optional tooltip content; omit to render without a hint icon.
   */
  hint?: {
    ariaLabel: string;
    content: string;
    testId?: string;
  };
  /**
   * Additional class names applied to the outer container.
   */
  cardClassName?: string;
  /**
   * Additional class names applied to the value text.
   */
  contentClassName?: string;
  /**
   * Optional test id for automation.
   */
  testId?: string;
  /**
   * Optional container test id for automation.
   */
  containerTestId?: string;
};

export function MetricCard({
  title,
  icon: Icon,
  value,
  hint,
  cardClassName,
  contentClassName,
  testId,
  containerTestId,
}: MetricCardProps) {
  return (
    <div
      className={cn("flex flex-col justify-center p-4 sm:p-6", cardClassName)}
      data-testid={containerTestId}
    >
      <div className="flex items-center gap-1.5 pb-2 sm:gap-2 sm:pb-3">
        <Icon
          className="text-brand size-4 shrink-0 opacity-80 sm:size-5"
          aria-hidden="true"
        />
        <span className="text-muted-foreground min-w-0 text-base font-medium sm:text-lg">
          {title}
        </span>
        {hint ? (
          <Hint
            ariaLabel={hint.ariaLabel}
            content={hint.content}
            testId={hint.testId}
          />
        ) : null}
      </div>
      <div
        className={cn(
          "text-foreground text-3xl tracking-tight sm:text-4xl",
          contentClassName,
        )}
        data-testid={testId}
      >
        {value}
      </div>
    </div>
  );
}
